/**
 * Permission System for WATERPROOF Management
 * Controls access to menu items and features
 */

/**
 * Check if user can access a feature
 * @param {string} role - User role (ADMIN, CUSTOMER)
 * @param {string} type - User type (MASTER, PRODUCCION, PROYECTOS, SAT1, SAT2, PROVEEDOR, CONTABILIDAD, PROGRAMMER for ADMIN; GENERAL, GROUP for CUSTOMER)
 * @param {string} feature - Feature to check
 * @returns {boolean}
 */
export function canAccess(role, type, category, feature) {
  const r = role;
  const t = type;
  const c = category;

  // Quick helpers
  
  // MAIN ROLES
  const isAdmin = r === 'ADMIN';
  const isCustomer = r === 'CUSTOMER';

  // CUSTOMER TYPES
  const isGeneral = t === 'GENERAL';
  const isGroup = t === 'GROUP';

  // ADMIN TYPES
  const isWorker = t === 'LIMITED'; // provisional
  const isProduccion = t === 'PRODUCCION';
  const isProyectos = t === 'PROYECTOS';
  const isSat1 = t === 'SAT1';
  const isSat2 = t === 'SAT2';
  const isProveedor = t === 'PROVEEDOR';
  const isContabilidad = t === 'CONTABILIDAD';
  const isProgrammer = t === 'PROGRAMMER';
  const isMaster = t === 'MASTER';

  // MASTER has access to everything
  if (isMaster) return true;

  // CONTABILIDAD has restricted access - only specific reports
  if (isContabilidad) {
    return ['sales', 'arqueos', 'arqueosSales', 'customers'].includes(feature);
  }

  // PRODUCCION has restricted access - only Machines and Modules
  if (isProduccion) {
    return ['machines', 'modules'].includes(feature);
  }

  // PROVEEDOR has restricted access - Modules
  if (isProveedor) {
    return ['modules'].includes(feature);
  }

  // PROGRAMMER has restricted access - Machines
  if (isProgrammer) {
    return ['machines'].includes(feature);
  }

  // Permission rules by feature
  switch (feature) {
    // Sales Channels - Puntos de Venta
    case 'machines':
      return (isAdmin && (isProyectos || isSat1 || isSat2)) || (isCustomer && (isGroup || isGeneral));
    case 'minis':
      return isAdmin && (isProyectos || isSat1 || isSat2);

    // Operations - Operativa
    case 'customers':
      return isAdmin && (isProyectos || isSat1 || isSat2);
    case 'contracts':
      return isAdmin && (isProyectos || isSat1 || isSat2);
    case 'incidences':
      return isAdmin && (isMaster || isSat1 || isSat2);
    case 'audit':
      return isAdmin && (isProyectos || isSat1 || isSat2);
    case 'modules':
      return isAdmin && (isProyectos || isSat1 || isSat2);
    case 'matrixs':
      return isAdmin && isProyectos;
    case 'tpvs':
      return isAdmin && (isProyectos || isSat2);

    // Sales - Ventas
    case 'sales':
      return (isAdmin && (isProyectos || isSat1 || isSat2)) || (isCustomer && (isGroup || isGeneral));
    case 'arqueos':
      return isAdmin && isProyectos;
    case 'arqueosSales':
      return isAdmin && isProyectos;
    case 'objectives':
      return isAdmin && isProyectos;
    case 'comissions':
      return isAdmin && isProyectos;

    // Time Tracking - Fichajes
    case 'shifts':
      return isAdmin && isProyectos;
    case 'shiftsAdmin':
      return isAdmin && isProyectos;

    // Administration - Administración
    case 'promoters':
      return isAdmin && isProyectos;
    case 'users':
      return isAdmin && isProyectos;
    case 'userAccess':
      return isAdmin && (isProyectos || isWorker);
    case 'logistics':
      return isAdmin && isProyectos;

    // Support - Soporte
    case 'supportticketadd':
      return isCustomer || (isAdmin && isSat1);
    case 'supporttickets':
      return isAdmin && (isMaster || isSat1);
    case 'supportchat':
      return (isAdmin && (isProyectos || isSat1 || isSat2)) || isCustomer;
    case 'faqs':
      return (isAdmin && (isProyectos || isSat1 || isSat2)) || isCustomer;
    case 'manuals':
      return (isAdmin && (isProyectos || isSat1 || isSat2)) || isCustomer;
    case 'api-docs':
      return (isAdmin && (isProyectos || isSat1 || isSat2)) || isCustomer;

    // SAT
    case 'slas':
      return isAdmin && (isMaster || isProyectos);
    case 'bowler':
      return isAdmin && (isMaster || isProyectos);

    default:
      return false;
  }
}

/**
 * Check if user has access to ANY feature in a list
 * @param {string} role - User role
 * @param {string} type - User type
 * @param {string} category - User category
 * @param {string[]} features - Array of features to check
 * @returns {boolean}
 */
export function hasAccessToAny(role, type, category, features) {
  return features.some(feature => canAccess(role, type, category, feature));
}

/**
 * Get user display name based on role and type
 * @param {string} role - User role
 * @param {string} type - User type
 * @returns {string}
 */
export function getUserRoleDisplay(role, type) {
  if (role === 'ADMIN') {
    switch (type) {
      case 'MASTER': return 'Super Admin';
      case 'PROYECTOS': return 'Proyectos';
      case 'SAT1': return 'SAT Nivel 1';
      case 'SAT2': return 'SAT Nivel 2';
      case 'PRODUCCION': return 'Producción';
      case 'PROVEEDOR': return 'Proveedor';
      case 'CONTABILIDAD': return 'Contabilidad';
      case 'PROGRAMMER': return 'Programador';
      default: return 'Administrador';
    }
  }
  if (role === 'CUSTOMER') {
    switch (type) {
      case 'GROUP': return 'Cliente Grupo';
      case 'GENERAL': return 'Cliente General';
      default: return 'Cliente';
    }
  }
  return role;
}

export default canAccess;
